import type { RefObject } from "react";
import React, { useEffect, useRef } from "react";

const DOT_GRID_GRADIENT_STOPS: readonly { offset: number; color: string }[] = [
  { offset: 0.4, color: "#F6F2EB" },
  { offset: 0.55, color: "#FF8819" },
  { offset: 0.66, color: "#FF339E" },
  { offset: 0.77, color: "#4E93FF" },
  { offset: 1, color: "#4E93FF" },
];

const DOT_GRID_GRADIENT_STOPS_MOBILE: readonly { offset: number; color: string }[] = [
  { offset: 0, color: "#FF8819" },
  { offset: 0.5, color: "#FF339E" },
  { offset: 1, color: "#4E93FF" },
];

const STEP_X_TARGET = 30;
const STEP_Y_TARGET = 30;
const BASE_R_TARGET = 9;
const MAX_BONUS_R_TARGET = 6;

const STEP_X_MOBILE = 20;
const STEP_Y_MOBILE = 22;
const BASE_R_MOBILE = 5;
const MAX_BONUS_R_MOBILE = 3;

const BASE_OPACITY = 0.8;

const MOUSE_SMOOTH = 0.09;
const POINTER_EXIT = { mx: -2500, my: -2500 };

const INTRO_STAGGER_S = 1.35;
const INTRO_COLUMN_DURATION_S = 1.05;

function hexToRgb(hex: string): [number, number, number] {
  const n = hex.slice(1);
  const v = parseInt(n, 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (x: number) => Math.max(0, Math.min(255, Math.round(x)));
  return `#${[clamp(r), clamp(g), clamp(b)].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

function lerpRgb(a: [number, number, number], b: [number, number, number], t: number): string {
  const x = Math.max(0, Math.min(1, t));
  return rgbToHex(
    a[0] + (b[0] - a[0]) * x,
    a[1] + (b[1] - a[1]) * x,
    a[2] + (b[2] - a[2]) * x,
  );
}

function sampleRadialGradientColor(x: number, y: number, w: number, h: number, isMobile: boolean): string {
  const cx = w / 2;
  const cy = h / 2;
  // Fixed size for desktop; stable floor for mobile
  const outerR = isMobile ? Math.max(w, 400) * 0.8 : 1250;
  const dist = Math.hypot(x - cx, y - cy);
  const u = Math.min(1, dist / outerR);
  const stops = isMobile ? DOT_GRID_GRADIENT_STOPS_MOBILE : DOT_GRID_GRADIENT_STOPS;
  if (u <= stops[0].offset) return stops[0].color;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (u <= b.offset) {
      const span = b.offset - a.offset || 1;
      const t = (u - a.offset) / span;
      return lerpRgb(hexToRgb(a.color), hexToRgb(b.color), t);
    }
  }
  return stops[stops.length - 1].color;
}

function easeOutCubic(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return 1 - (1 - x) ** 3;
}

function smoothstep01(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

function introReveal01(x: number, w: number, elapsedS: number, reduceMotion: boolean): number {
  if (reduceMotion) return 1;
  const distFromCenter = Math.abs(x - w / 2);
  const stagger = (distFromCenter / (w / 2)) * INTRO_STAGGER_S;
  const t = (elapsedS - stagger) / INTRO_COLUMN_DURATION_S;
  return easeOutCubic(smoothstep01(t));
}

function radiusForDistance(d: number, baseR: number, maxBonusR: number, influenceR: number): number {
  if (d >= influenceR) return baseR;
  const t = 1 - d / influenceR;
  return baseR + maxBonusR * smoothstep01(t);
}

function opacityForDistance(d: number, baseAlpha: number, influenceR: number): number {
  if (d >= influenceR) return baseAlpha;
  const t = 1 - d / influenceR;
  return baseAlpha + (1 - baseAlpha) * smoothstep01(t);
}

type HeroInteractiveDotGridProps = {
  rootRef: RefObject<HTMLElement | null>;
  className?: string;
};

export function HeroInteractiveDotGrid({ rootRef, className }: HeroInteractiveDotGridProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef<{ mx: number; my: number } | null>(null);
  const smoothMouseRef = useRef<{ mx: number; my: number } | null>(null);
  const lastClientRef = useRef<{ x: number; y: number } | null>(null);
  const rafRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let setupRafId = 0;
    let teardown: (() => void) | undefined;

    const run = (attempt = 0): void => {
      if (cancelled) return;
      const root = rootRef.current;
      const canvas = canvasRef.current;
      if (!root || !canvas) {
        if (attempt < 48) {
          setupRafId = requestAnimationFrame(() => run(attempt + 1));
        }
        return;
      }

      const reduceMotion =
        typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      const ctx = canvas.getContext("2d", { alpha: true });
      if (!ctx) return;

      const introStartMs = performance.now();

      const resize = () => {
        const wrapper = canvas.parentElement;
        if (!wrapper) return;
        canvas.style.width = "";
        canvas.style.height = "";
        const rect = wrapper.getBoundingClientRect();
        const pl = parseFloat(getComputedStyle(wrapper).paddingLeft) || 0;
        const dpr = Math.min(window.devicePixelRatio ?? 1, 2);
        const w = Math.max(1, rect.width - pl);
        const h = Math.max(1, rect.height);
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      };

      let scrollSyncRafId = 0;
      const syncPointerFromClient = (clientX: number, clientY: number) => {
        lastClientRef.current = { x: clientX, y: clientY };
        const hero = root.getBoundingClientRect();

        const inHero =
          clientX >= hero.left &&
          clientX <= hero.right &&
          clientY >= hero.top &&
          clientY <= hero.bottom;

        const navEl = typeof document !== "undefined" ? document.querySelector("nav") : null;
        const navR = navEl?.getBoundingClientRect();
        const inNav =
          !!navR &&
          clientX >= navR.left &&
          clientX <= navR.right &&
          clientY >= navR.top &&
          clientY <= navR.bottom;

        const mainEl = typeof document !== "undefined" ? document.querySelector("main") : null;
        const mainR = mainEl?.getBoundingClientRect();
        const inMainBelowHero =
          !!mainR &&
          clientX >= mainR.left &&
          clientX <= mainR.right &&
          clientY > hero.bottom &&
          clientY >= mainR.top &&
          clientY <= mainR.bottom;

        if (!inHero && !inNav && !inMainBelowHero) {
          mouseRef.current = null;
          return;
        }

        let sx = clientX;
        let sy = clientY;
        if (inNav && !inHero) {
          sx = Math.min(Math.max(clientX, hero.left), hero.right);
          sy = hero.top + 2;
        } else if (inMainBelowHero && !inHero) {
          sx = Math.min(Math.max(clientX, hero.left), hero.right);
          sy = hero.bottom - 2;
        }

        const canvasRect = canvas.getBoundingClientRect();
        if (canvasRect.width <= 0) return;
        const lx = sx - canvasRect.left;
        const ly = sy - canvasRect.top;
        mouseRef.current = { mx: lx, my: ly };
      };

      const onWindowMouseMove = (e: MouseEvent) => {
        syncPointerFromClient(e.clientX, e.clientY);
      };

      const onScrollSync = () => {
        if (scrollSyncRafId) return;
        scrollSyncRafId = requestAnimationFrame(() => {
          scrollSyncRafId = 0;
          const lc = lastClientRef.current;
          if (lc) syncPointerFromClient(lc.x, lc.y);
        });
      };

      const stepSmoothPointer = () => {
        if (reduceMotion) {
          smoothMouseRef.current = null;
          return;
        }
        const raw = mouseRef.current;
        const sm = smoothMouseRef.current;
        const k = MOUSE_SMOOTH;
        if (raw) {
          if (!sm) {
            smoothMouseRef.current = { mx: raw.mx, my: raw.my };
          } else {
            sm.mx += (raw.mx - sm.mx) * k;
            sm.my += (raw.my - sm.my) * k;
          }
          return;
        }
        if (sm) {
          sm.mx += (POINTER_EXIT.mx - sm.mx) * k;
          sm.my += (POINTER_EXIT.my - sm.my) * k;
          if (Math.hypot(sm.mx - POINTER_EXIT.mx, sm.my - POINTER_EXIT.my) < 80) {
            smoothMouseRef.current = null;
          }
        }
      };

      const draw = () => {
        const rect = canvas.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        if (w <= 0 || h <= 0) return;

        const isMobile = w < 1024;
        const dpr = canvas.width / w;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        const elapsedS = (performance.now() - introStartMs) / 1000;
        stepSmoothPointer();
        const m = reduceMotion ? null : smoothMouseRef.current;

        // Dynamic Parameters
        const dotR = isMobile ? BASE_R_MOBILE : BASE_R_TARGET;
        const maxBonusR = isMobile ? MAX_BONUS_R_MOBILE : MAX_BONUS_R_TARGET;
        const maxDotR = dotR + maxBonusR;
        const margin = maxDotR + 2;

        const stepX = isMobile ? STEP_X_MOBILE : STEP_X_TARGET;
        const stepY = isMobile ? STEP_Y_MOBILE : STEP_Y_TARGET;

        const cols = Math.max(2, Math.floor((w - 2 * margin) / stepX) + 1);
        const rows = Math.max(2, Math.floor((h - 2 * margin) / stepY) + 1);

        const actualStepX = cols > 1 ? (w - 2 * margin) / (cols - 1) : 0;
        const actualStepY = rows > 1 ? (h - 2 * margin) / (rows - 1) : 0;

        const influenceR = isMobile ? 120 : 250;
        const aspect = h / w;

        for (let iy = 0; iy < rows; iy++) {
          for (let ix = 0; ix < cols; ix++) {
            const x = margin + ix * actualStepX;
            const y = margin + iy * actualStepY;

            // Fading logic for mobile only
            let mask = 1;
            if (isMobile) {
              const relX = x / w;
              const relY = y / h;
              const aspect = h / w;

              // Distance from corners (aspect corrected)
              const distTL = Math.hypot(relX, relY * aspect);
              const distBR = Math.hypot(1 - relX, (1 - relY) * aspect);

              // Both clusters expanded to fill more space in mobile
              const fadeRadiusTL = 0.85;
              const fadeRadiusBR = 0.85;

              const factorTL = Math.max(0, 1 - (distTL / fadeRadiusTL));
              const factorBR = Math.max(0, 1 - (distBR / fadeRadiusBR));

              mask = Math.max(factorTL, factorBR);
              mask = mask * mask * (3 - 2 * mask); // Smoothstep01
              if (mask <= 0.05) continue;
            }

            let r = dotR;
            let alpha = BASE_OPACITY;

            if (m) {
              const dx = x - m.mx;
              const dy = y - m.my;
              const dist = Math.hypot(dx, dy);
              r = radiusForDistance(dist, dotR, maxBonusR, influenceR);
              alpha = opacityForDistance(dist, BASE_OPACITY, influenceR);
            }

            r *= mask;
            const intro = introReveal01(x, w, elapsedS, reduceMotion);
            r *= intro;
            alpha *= mask * intro;

            if (r < 0.5 || alpha < 0.01) continue;

            ctx.globalAlpha = alpha;
            ctx.fillStyle = sampleRadialGradientColor(x, y, w, h, isMobile);
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      };

      const tick = () => {
        draw();
        if (!reduceMotion) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };

      let resizeDrawRaf = 0;
      const scheduleResizeDraw = (): void => {
        if (resizeDrawRaf) return;
        resizeDrawRaf = requestAnimationFrame(() => {
          resizeDrawRaf = 0;
          resize();
          draw();
          const lc = lastClientRef.current;
          if (lc) syncPointerFromClient(lc.x, lc.y);
        });
      };

      resize();
      const ro = new ResizeObserver(scheduleResizeDraw);
      ro.observe(root);
      const padded = canvas.parentElement;
      if (padded) ro.observe(padded);

      window.addEventListener("resize", scheduleResizeDraw, { passive: true });
      const vv = typeof window !== "undefined" ? window.visualViewport : null;
      if (vv) {
        vv.addEventListener("resize", scheduleResizeDraw);
        vv.addEventListener("scroll", scheduleResizeDraw);
      }

      window.addEventListener("mousemove", onWindowMouseMove, { passive: true });
      window.addEventListener("scroll", onScrollSync, { passive: true, capture: true });

      if (reduceMotion) {
        draw();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }

      teardown = () => {
        ro.disconnect();
        window.removeEventListener("resize", scheduleResizeDraw);
        if (vv) {
          vv.removeEventListener("resize", scheduleResizeDraw);
          vv.removeEventListener("scroll", scheduleResizeDraw);
        }
        window.removeEventListener("mousemove", onWindowMouseMove);
        window.removeEventListener("scroll", onScrollSync, true);
        cancelAnimationFrame(scrollSyncRafId);
        cancelAnimationFrame(resizeDrawRaf);
        cancelAnimationFrame(rafRef.current);
        lastClientRef.current = null;
        mouseRef.current = null;
        smoothMouseRef.current = null;
      };
    };

    run();

    return () => {
      cancelled = true;
      cancelAnimationFrame(setupRafId);
      teardown?.();
    };
  }, [rootRef]);

  return (
    <div
      className={`pointer-events-none absolute inset-0 z-0 min-h-0 w-full min-w-0 overflow-hidden ${className ?? ""}`}
      aria-hidden
    >
      <div className="relative h-full min-h-0 w-full min-w-0 overflow-hidden">
        <canvas ref={canvasRef} className="block h-full w-full" />
      </div>
    </div>
  );
}
